import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dumbbell } from 'lucide-react';
import { toast } from 'sonner';

const Login = ({ onLogin }) => {
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post('/auth/login', {
        username: formData.username,
        password: formData.password
      });
      toast.success('Inicio de sesión exitoso');
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en la operación');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post('/auth/forgot-password', { email: forgotEmail });
      toast.success('Si el email existe, recibirás instrucciones para restablecer tu contraseña');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="login-branding">
          <div className="brand-icon">
            <Dumbbell size={48} strokeWidth={2.5} />
          </div>
          <h1 className="brand-title">Vielman Fitness GYM</h1>
          <p className="brand-subtitle">Sistema de Gestión Integral</p>
        </div>
        
        <div className="features-list">
          <div className="feature-item">
            <div className="feature-icon">📦</div>
            <div>
              <h3>Control de Inventario</h3>
              <p>Gestiona productos y stock</p>
            </div>
          </div>
          <div className="feature-item">
            <div className="feature-icon">💳</div>
            <div>
              <h3>Suscripciones</h3>
              <p>Administra membresías de clientes</p>
            </div>
          </div>
          <div className="feature-item">
            <div className="feature-icon">📊</div>
            <div>
              <h3>Informes Detallados</h3>
              <p>Analiza ingresos y gastos</p>
            </div>
          </div>
        </div>
      </div>

      <div className="login-right">
        <Card className="login-card">
          {!showForgotPassword ? (
            <>
              <div className="login-header">
                <h2>Iniciar Sesión</h2>
                <p>Ingresa tus credenciales para continuar</p>
              </div>

              <form onSubmit={handleSubmit} className="login-form">
                <div className="form-group">
                  <Label htmlFor="username">Usuario</Label>
                  <Input
                    id="username"
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    required
                    data-testid="username-input"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="password">Contraseña</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    data-testid="password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="submit-btn" 
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? 'Procesando...' : 'Iniciar Sesión'}
                </Button>

                <div className="form-footer">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="forgot-password-btn"
                    data-testid="forgot-password-btn"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className="login-header">
                <h2>Recuperar Contraseña</h2>
                <p>Ingresa tu email para recibir instrucciones</p>
              </div>

              <form onSubmit={handleForgotPasswordSubmit} className="login-form">
                <div className="form-group">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="tu@email.com"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="submit-btn" 
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Enviando...' : 'Enviar Instrucciones'}
                </Button>

                <div className="form-footer">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="forgot-password-btn"
                    data-testid="back-to-login-btn"
                  >
                    Volver al inicio de sesión
                  </button>
                </div>
              </form>
            </>
          )}
        </Card>
      </div>

      <style jsx>{`
        .login-container {
          display: flex;
          min-height: 100vh;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }

        .login-left {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 4rem;
          color: white;
        }

        .login-branding {
          margin-bottom: 4rem;
        }

        .brand-icon {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .brand-title {
          font-size: 3rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
          letter-spacing: -0.02em;
        }

        .brand-subtitle {
          font-size: 1.25rem;
          opacity: 0.9;
          font-weight: 500;
        }

        .features-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.5rem;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .feature-icon {
          font-size: 2rem;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
        }

        .feature-item h3 {
          font-size: 1.125rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
        }

        .feature-item p {
          opacity: 0.9;
          font-size: 0.875rem;
        }

        .login-right {
          flex: 0 0 500px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          background: white;
        }

        .login-card {
          width: 100%;
          max-width: 420px;
          padding: 2.5rem;
          border: none;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
        }

        .login-header {
          margin-bottom: 2rem;
        }

        .login-header h2 {
          font-size: 1.875rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .login-header p {
          color: #64748b;
          font-size: 0.875rem;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .submit-btn {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          padding: 0.75rem;
          font-weight: 600;
          margin-top: 0.5rem;
          height: 48px;
          border-radius: 8px;
        }

        .submit-btn:hover {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
        }

        .form-footer {
          text-align: center;
          margin-top: 0.5rem;
        }

        .forgot-password-btn {
          background: none;
          border: none;
          color: #10b981;
          font-weight: 600;
          cursor: pointer;
          font-size: 0.875rem;
        }

        .forgot-password-btn:hover {
          text-decoration: underline;
        }

        @media (max-width: 1024px) {
          .login-left {
            display: none;
          }

          .login-right {
            flex: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;
